/**
 * The tile a reply's `create_artifact` or `update_artifact` row becomes once
 * it has saved: the artifact's title, what it is and which version, opening
 * it beside the chat. Where nothing can open it (`OpenArtifact` unset), the
 * tile is drawn and does nothing.
 */

import { createContext, useContext } from 'react';
import type { ArtifactKind, ToolRun } from '../api';
import { Ph } from './ui';

/** Opens an artifact, at a version, beside the chat. */
export const OpenArtifact = createContext<((id: string, version: number) => void) | null>(null);

export const ARTIFACT_ICON: Record<ArtifactKind, string> = {
  html: 'browser',
  svg: 'bezier-curve',
  markdown: 'file-text',
  quiz: 'list-checks',
  react: 'atom'
};

export const ARTIFACT_KIND_LABEL: Record<ArtifactKind, string> = {
  html: 'Page',
  svg: 'Image',
  markdown: 'Document',
  quiz: 'Quiz',
  react: 'Component'
};

export function ArtifactTile({ artifact }: { artifact: NonNullable<ToolRun['artifact']> }) {
  const open = useContext(OpenArtifact);
  return (
    <button
      type="button"
      className="bare hov-lift"
      disabled={!open}
      onClick={() => open?.(artifact.id, artifact.version)}
      style={{
        alignSelf: 'flex-start',
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        padding: '12px 16px 12px 12px',
        borderRadius: 'var(--radius-app-card)',
        background: 'var(--surface-card)',
        minWidth: 'min(280px, 100%)',
        maxWidth: '100%',
        boxSizing: 'border-box',
        cursor: open ? 'pointer' : 'default'
      }}
    >
      <span
        style={{
          width: 36,
          height: 36,
          flex: 'none',
          borderRadius: 9,
          background: 'var(--primary-soft)',
          color: 'var(--primary)',
          display: 'grid',
          placeItems: 'center'
        }}
      >
        <Ph name={ARTIFACT_ICON[artifact.kind]} size={18} />
      </span>
      <span style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
        <span style={{ maxWidth: '100%', fontSize: 14, fontWeight: 600, color: 'var(--text-strong)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {artifact.title}
        </span>
        <span style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>
          {ARTIFACT_KIND_LABEL[artifact.kind]} · version {artifact.version}
        </span>
      </span>
      {open ? <Ph name="arrow-up-right" size={15} color="var(--text-muted)" /> : null}
    </button>
  );
}
